import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { KEYS } from "app/core/data/core.const";
import { PaginatedList } from "app/core/models/paginated-list.types";
import { SnackbarService } from "app/core/services/snackbar.service";
import { Observable } from "rxjs";
import { FileUpload } from "app/modules/file-upload/file-upload.types";
import { ActionStage, CreatePersonReceive, DocumentToBePrinted, ExistingIdNumberResponse, GenerateDocument, Person, PersonFilter, PersonIdNumber, PersonListDto, UpdatePersonProcess, UpdatePersonReceive, UpdatePersonReport, UpdatePersonReportWithFieldChanges, UpdatePersonSchedule, UpdatePersonsCourierNumbers, UpdatePersonStatus, UpdatePriceAndInvoiceNumber } from "./person.types";
import { Verification } from "./verification-document/verification-document.model";

@Injectable()
export class PersonService {
    constructor(
        private _httpClient: HttpClient,
        private snackbarService: SnackbarService) {
    }

    getPersons = (filter: PersonFilter): Observable<PaginatedList<Person>> =>
        this._httpClient.post('api/persons', filter).alertError(this.snackbarService, 'Failed to get persons');

    getPersonsList = (filter: PersonFilter): Observable<PaginatedList<PersonListDto>> =>
        this._httpClient.post('api/persons/list', filter).alertError(this.snackbarService, 'Failed to get persons');


    getPerson = (id: string): Observable<Person> =>
        this._httpClient.get(`api/persons/${id}`)
            .alertError(this.snackbarService, 'Failed to get person');

    getPersonDocumentsToBePrinted = (id: string): Observable<Array<DocumentToBePrinted>> =>
        this._httpClient.get<Array<DocumentToBePrinted>>(`api/persons/${id}/document/to-be-printed`)
            .alertError(this.snackbarService, 'Failed to get person documents to be printed');

    getExistingIdNumbers = (): Observable<Array<PersonIdNumber>> =>
        this._httpClient.get(`api/persons/existingIdNumbers`)
            .alertError(this.snackbarService, 'Failed to get existing id numbers');

    findExistingIdNumber = (personId: string, idNumber: string): Observable<ExistingIdNumberResponse> =>
        this._httpClient.get<ExistingIdNumberResponse>(`api/persons/idNumber/find/${personId}/${idNumber}`);

    findExistingRegisterNumber = (personId: string, registerNumber: string): Observable<boolean> =>
        this._httpClient.get(`api/persons/registerNumber/find/${personId}/${registerNumber}`)
            .alertError(this.snackbarService, 'Failed to find existing register number');

    findPerson(id: string): Observable<boolean> {
        return this._httpClient.get<boolean>(`api/persons/${id}/find`);
    }

    createPersonBarcode = (id: string, confirmed: boolean): Observable<any> =>
        this._httpClient.post(`api/persons/createBarcode`, { personId: id, confirmed: confirmed })
            .alertError(this.snackbarService, 'Failed to create person barcode');

    createPersonLabels = (id: string, quantity: number, confirmed: boolean): Observable<any> =>
        this._httpClient.post(`api/persons/createPersonUrnLabels`, { personId: id, quantity: quantity, confirmed: confirmed})
            .alertError(this.snackbarService, 'Failed to create person urn labels');

    getQrCode = (id: string, actionStage: ActionStage): Observable<string> =>
        this._httpClient.get<string>(`api/persons/${id}/qr`, { params: { actionStage } });

    createPersonReceive = (request: CreatePersonReceive): Observable<string> =>
        this._httpClient.post<string>(`api/persons/receive`, request)
            .alertError(this.snackbarService, 'Failed to create person');

    markPersonInstructionsCompleted = (data: any): Observable<any> =>
        this._httpClient.put(`api/persons/instructions/markCompleted`, data)
            .alertError(this.snackbarService, 'Failed to mark instructions as completed');

    updatePersonReceive = (request: UpdatePersonReceive): Observable<void> =>
        this._httpClient.put<void>(`api/persons/${request.id}/receive`, request)
            .alertError(this.snackbarService, 'Failed to update person');

    updatePersonPriceAndInvoiceNumber = (request: UpdatePriceAndInvoiceNumber): Observable<void> =>
        this._httpClient.put<void>(`api/persons/${request.personId}/priceAndInvoiceNumber`, request)
            .alertError(this.snackbarService, 'Failed to update price and/or invoice number');

    updatePersonPriceAndInvoiceNumberWithFieldChanges = (request: UpdatePriceAndInvoiceNumber): Observable<void> =>
        this._httpClient.put<void>(`api/persons/${request.personId}/priceAndInvoiceNumber/field-changes`, request)
            .alertError(this.snackbarService, 'Failed to update price and/or invoice number');

    removePerson = (id: string): Observable<void> =>
        this._httpClient.put<void>(`api/persons/${id}/remove`, {personId: id})
            .alertError(this.snackbarService, 'Failed to remove person');

    updatePersonProcess = (request: UpdatePersonProcess): Observable<void> =>
        this._httpClient.put<void>(`api/persons/${request.id}/process`, request)
            .alertError(this.snackbarService, 'Failed to update person');

    updatePersonSchedule = (request: UpdatePersonSchedule): Observable<void> =>
        this._httpClient.put<void>(`api/persons/${request.id}/schedule`, request)
            .alertError(this.snackbarService, 'Failed to update person');

    updatePersonReport = (request: UpdatePersonReport): Observable<void> =>
        this._httpClient.put<void>(`api/persons/${request.id}/report`, request)
            .alertError(this.snackbarService, 'Failed to update person');

    updatePersonReportWithFieldChanges = (request: UpdatePersonReportWithFieldChanges): Observable<void> =>
        this._httpClient.put<void>(`api/persons/${request.id}/report/field-changes`, request)
            .alertError(this.snackbarService, 'Failed to update person');

    updatePersonsCourierNumbers = (request: UpdatePersonsCourierNumbers): Observable<void> =>
        this._httpClient.put<void>(`api/persons/update/courierNumbers`, request)
            .alertError(this.snackbarService, 'Failed to update persons courier numbers');

    updatePersonStatus = (request: UpdatePersonStatus): Observable<void> =>
        this._httpClient.put<void>(`api/persons/${request.personId}/status`, request)
            .alertError(this.snackbarService, 'Failed to update person status');

    generateDocument = (request: GenerateDocument): Observable<Blob> =>
        this._httpClient.put(`api/persons/${request.personId}/document`, request, { responseType: 'blob' })
            .alertError(this.snackbarService, 'Failed to generate document');

    generateCertificateEnvelope = (personId: string, confirmed: boolean): Observable<Blob> =>
        this._httpClient.put(`api/persons/${personId}/document/certificate/envelope`, { personId, confirmed } , { responseType: 'blob' })

    setFilter = (actionStage: ActionStage, filter: PersonFilter) =>
        sessionStorage.setItem(`${KEYS.FILTER}-${actionStage.toString()}`, JSON.stringify(filter));

    clearFilter(actionStage: ActionStage) {
        sessionStorage.removeItem(`${KEYS.FILTER}-${actionStage.toString()}`);
    }

    getFilter(actionStage: ActionStage, pageSize: number = 10): PersonFilter {
        const filterString = sessionStorage.getItem(`${KEYS.FILTER}-${actionStage.toString()}`);

        if (!filterString) {
            const filter: PersonFilter = {
                pageNumber: 1,
                pageSize,
                sortDirection: null,
                query: null,
                sortBy: null,
                dateReceivedFrom: null,
                dateReceivedTo: null,
                undertakerId: null,
                doctorId: null,
                outstandingDocuments: [],
                receivedDocumentTypes: [],
                receivedDocumentTypesIds: [],
                actionStage,
                personStatusId: null,
                isVerified: null
            };

            return filter;
        }

        return JSON.parse(filterString);
    }

    genatePersonsExcelFile(filter: PersonFilter): Observable<any> {
        return this._httpClient.post<any>(`api/persons/document/excel`, filter)
            .alertError(this.snackbarService, 'Failed to generate excel file');
    }

    generateCremationCertificate = (id: string, confirmed: boolean): Observable<Blob> =>
        this._httpClient.put(`api/persons/${id}/document/certificate/cremation`, {id, confirmed} , { responseType: 'blob' })

    generateControlSheet = (id: string, confirmed: boolean): Observable<Blob> =>
        this._httpClient.put(`api/persons/${id}/document/controlSheet`, { id: id, confirmed: confirmed }  , { responseType: 'blob' })

    uploadVerificationDocuments(id: string, files: File[]): Observable<Verification[]> {
        const formData = new FormData();
        formData.append('PersonId', id);
        files.forEach(f => formData.append('Files', f, f.name));

        return this._httpClient.post<Verification[]>(`api/persons/${id}/verification-documents`, formData)
            .alertError(this.snackbarService, 'Failed to upload verification documents');
    }

    deleteVerificationDocument(id: number): Observable<void> {
        return this._httpClient.delete<void>(`api/persons/${id}/verification-document`)
            .alertError(this.snackbarService, 'Failed to delete verification document');
    }
}
